import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import isEqual from 'lodash.isequal';
import groupBy from 'lodash.groupby';
import { DateTime } from 'luxon';
import { sprintf } from 'sprintf-js';
import { Link } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableHeader,
  TableHeaderColumn,
  TableRow,
  TableRowColumn
} from 'material-ui/Table';
import { URL_PROJECT_STATS } from '../constants.js';
import fetcher from '../helpers/fetcher';
import {
  STATUSES_CALLED,
  STATUSES_POSITIVE,
  STATUSES_NEGATIVE,
  STATUSES_CALLBACK_SCHEDULE,
  STATUSES_INVALID,
  STATUSES
} from '../helpers/contacts';
import StatsSection from './StatsSection';
import { LUXTON_FORMAT_DATE } from '../helpers/date.js';

const dataTransformer = data => key => ({
  name: key,
  value: data[key]
});

class ProjectStats extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      name: '',
      statuses: [],
      contacted: [],
      increases: [],
      increasesTotal: 0,
      categories: [],
      agents: [],
      projectsData: [],
      loading: false
    };
  }

  componentDidMount() {
    this._loadStats();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.updatedAtAfter !== this.props.updatedAtAfter ||
      prevProps.updatedAtBefore !== this.props.updatedAtBefore ||
      !isEqual((prevProps.record || {}).id, (this.props.record || {}).id)
    ) {
      this._loadStats();
    }
  }

  _loadStats = () => {
    this.setState({ loading: true });
    fetcher
      .post(`${URL_PROJECT_STATS}`, {
        id: this.props.record.id,
        groupped: this.props.groupped,
        extended: true,
        calledAtAfter: this.props.updatedAtAfter,
        calledAtBefore: this.props.updatedAtBefore
      })
      .then(response => {
        this.setState({ loading: false });
        const callDays = ~~get(response, 'data.data[0].callDays', 0);
        const total = get(response, 'data.data[0].all', 0);
        const remaining = get(response, 'data.data[0].notDone', 0);
        const called = total - remaining;
        const calledPerPeriod = get(
          response,
          'data.data[0].calledPerPeriod',
          0
        );
        const statuses = get(response, 'data.data[0].statuses', {});
        const increases = get(response, 'data.data[0].increases', []);
        const reactivations = get(response, 'data.data[0].reactivations', 0);
        const agentStatusData = get(response, 'data.data[0].agents', []);
        const dataTransformerStatuses = dataTransformer(statuses);
        const categories = Object.keys(statuses).reduce(
          (acc, status) => {
            if (status === 'positive_agreement') {
              acc.positive_agreement =
                acc.positive_agreement + statuses[status];
            } else if (STATUSES_POSITIVE.indexOf(status) > -1) {
              acc.positive = acc.positive + statuses[status];
            } else if (STATUSES_NEGATIVE.indexOf(status) > -1) {
              acc.negative = acc.negative + statuses[status];
            } else if (STATUSES_CALLBACK_SCHEDULE.indexOf(status) > -1) {
              acc.callback = acc.callback + statuses[status];
            } else if (STATUSES_INVALID.indexOf(status) > -1) {
              acc.invalid = acc.invalid + statuses[status];
            }
            return acc;
          },
          {
            invalid: 0,
            negative: 0,
            callback: 0,
            positive: 0,
            positive_agreement: 0
          }
        );
        const increasesTotal = increases.reduce(
          (sum, item) => sum + item.increase * item.count,
          0
        );
        const positiveAgreement = statuses['positive_agreement'];

        const agentStatusByUsername = groupBy(agentStatusData, 'username');
        this.setState({
          name: get(response, 'data.data[0].name', ''),
          projectsData: get(response, 'data.data[0].projectsData', []),
          callDays,
          progress: total > 0 ? (called / total) * 100 : 0,
          called,
          calledPerPeriod,
          total,
          remaining,
          reactivations,
          increasesNumber: increases.reduce(
            (sum, item) => sum + ~~item.count,
            0
          ),
          increasesTotal,
          increases: increases.map(increaseItem => ({
            name: increaseItem.increase,
            value: ~~increaseItem.count
          })),
          motivatedCalls: categories.positiveAgreement + categories.negative,
          positiveAgreement,
          conversionRate:
            (positiveAgreement / (positiveAgreement + categories.negative)) *
            100,
          averageContribution: positiveAgreement
            ? increasesTotal / positiveAgreement
            : 0,
          statuses: Object.keys(statuses).map(dataTransformerStatuses),
          contacted: Object.keys(statuses)
            .filter(status => STATUSES_CALLED.indexOf(status) > -1)
            .map(dataTransformerStatuses),
          categories: Object.keys(categories).map(dataTransformer(categories)),
          agents: Object.keys(agentStatusByUsername).reduce((agg, username) => {
            agg.push({
              name: username,
              value: agentStatusData.reduce((sum, agentStatus) => {
                if (
                  agentStatus.username === username &&
                  agentStatus.status === 'positive_agreement'
                ) {
                  return sum + ~~agentStatus.count;
                }
                return sum;
              }, 0)
            });
            return agg;
          }, [])
        });
      })
      .catch(e => {
        this.setState({ loading: false });
      });
  };

  /*

          // motivatedCalls: 0 // sum of all negative + sum or all positives


   number of calldays | average number of motivated calls                             | average positive agreements (number)     | average positive agreements (%) | average increase
to be filled in by me | sum of positive and negative agreements / number of calldays  | positive agreements / number of calldays | idem, but %                     | total increase / number of calldays


* increasesTotal is the total sum or the total number of increases

And then

* per project
* total


And also

* week
* month
* year

  */

  render() {
    const titleParts = [];
    if (this.props.updatedAtAfter) {
      titleParts.push(
        DateTime.fromJSDate(this.props.updatedAtAfter).toFormat(
          LUXTON_FORMAT_DATE
        )
      );
    }
    if (this.props.updatedAtBefore) {
      titleParts.push(
        DateTime.fromJSDate(this.props.updatedAtBefore).toFormat(
          LUXTON_FORMAT_DATE
        )
      );
    }
    return (
      <div>
        {this.state.projectsData.length > 0 && (
          <div>
            <h1>Projects</h1>
            <ul>
              {this.state.projectsData.map(projectData => (
                <li>
                  <Link
                    to={{
                      pathname: `/projects/${projectData.id}/show`
                    }}
                  >
                    {projectData.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
        {this.props.children}
        <h1>General Stats {this.state.name}</h1>
        <Table>
          <TableHeader
            displayRowCheckbox={false}
            adjustForCheckbox={false}
            displaySelectAll={false}
          >
            <TableRow>
              <TableHeaderColumn>Progress</TableHeaderColumn>
              <TableHeaderColumn>Contacted</TableHeaderColumn>
              <TableHeaderColumn>Total Contacts</TableHeaderColumn>
              <TableHeaderColumn>Reactivations</TableHeaderColumn>
            </TableRow>
          </TableHeader>
          <TableBody displayRowCheckbox={false}>
            <TableRow>
              <TableRowColumn>
                {Math.round(this.state.progress)}%
              </TableRowColumn>
              <TableRowColumn>{this.state.called}</TableRowColumn>
              <TableRowColumn>{this.state.total} </TableRowColumn>
              <TableRowColumn>{this.state.reactivations || 0} </TableRowColumn>
            </TableRow>
          </TableBody>
        </Table>
        <h1>Stats {titleParts.join(' - ')}</h1>
        {this.state.callDays > 0 && (
          <Table>
            <TableHeader
              displayRowCheckbox={false}
              adjustForCheckbox={false}
              displaySelectAll={false}
            >
              <TableRow>
                <TableHeaderColumn>Days Number</TableHeaderColumn>
                <TableHeaderColumn>
                  Avg. number of motivated calls
                </TableHeaderColumn>
                <TableHeaderColumn>
                  Avg. positive agreements (#)
                </TableHeaderColumn>
                <TableHeaderColumn>
                  Avg. positive agreements (%)
                </TableHeaderColumn>
                <TableHeaderColumn>Avg. Increase</TableHeaderColumn>
              </TableRow>
            </TableHeader>
            <TableBody displayRowCheckbox={false}>
              <TableRow>
                <TableRowColumn>{this.state.callDays}</TableRowColumn>
                <TableRowColumn>
                  {sprintf(
                    '%0.2d',
                    ~~this.state.motivatedCalls / this.state.callDays
                  )}
                </TableRowColumn>
                <TableRowColumn>
                  {sprintf(
                    '%0.2d',
                    this.state.positiveAgreement / this.state.callDays
                  )}
                </TableRowColumn>
                <TableRowColumn>
                  {sprintf(
                    '%0.2d%%',
                    this.state.motivatedCalls
                      ? this.state.positiveAgreement / this.state.motivatedCalls
                      : 0
                  )}
                </TableRowColumn>
                <TableRowColumn>
                  {sprintf(
                    '%0.2d',
                    this.state.increasesTotal / this.state.callDays
                  )}
                </TableRowColumn>
              </TableRow>
            </TableBody>
          </Table>
        )}
        <Table>
          <TableHeader
            displayRowCheckbox={false}
            adjustForCheckbox={false}
            displaySelectAll={false}
          >
            <TableRow>
              <TableHeaderColumn>Contacted</TableHeaderColumn>
              <TableHeaderColumn>Number Increases</TableHeaderColumn>
              <TableHeaderColumn>Total Increase Amount</TableHeaderColumn>
              <TableHeaderColumn>Conversion Rate</TableHeaderColumn>
              <TableHeaderColumn>Average Contribution</TableHeaderColumn>
            </TableRow>
          </TableHeader>
          <TableBody displayRowCheckbox={false}>
            <TableRow>
              <TableRowColumn>{this.state.calledPerPeriod}</TableRowColumn>
              <TableRowColumn>{this.state.increasesNumber}</TableRowColumn>
              <TableRowColumn>{this.state.increasesTotal}&euro;</TableRowColumn>
              <TableRowColumn>
                {sprintf('%0.2d', ~~this.state.conversionRate)}%
              </TableRowColumn>
              <TableRowColumn>
                {this.state.averageContribution}&euro;
              </TableRowColumn>
            </TableRow>
          </TableBody>
        </Table>
        <StatsSection
          title="Positive Agreement per Agent"
          data={this.state.agents}
        />
        <StatsSection title="Contacted" data={this.state.contacted} />
        <StatsSection title="Status" data={this.state.statuses} />
        <StatsSection
          title="Increases"
          data={this.state.increases}
          total={{ name: 'Total', value: this.state.increasesTotal }}
        />
        <StatsSection title="Category" data={this.state.categories} />
      </div>
    );
  }
}

ProjectStats.propTypes = {
  record: PropTypes.shape({
    id: PropTypes.number
  }),
  groupped: PropTypes.bool,
  updatedAtAfter: PropTypes.object,
  updatedAtBefore: PropTypes.object
};

export default ProjectStats;



// WEBPACK FOOTER //
// ./src/components/ProjectStats.js